import {FC, PropsWithChildren, useEffect, useMemo, useRef} from "react";
import {useEnvironment} from "@common-core/react-runtime/context";
import {useOktaAuth} from "@okta/okta-react";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split
} from "@apollo/client";
import {removeTypenameFromVariables} from "@apollo/client/link/remove-typename";
import {useFlags} from "launchdarkly-react-client-sdk";
import {AUTH_TYPE} from "aws-appsync-auth-link";
import {createSubscriptionHandshakeLink} from "aws-appsync-subscription-link";
import {getMainDefinition} from "@apollo/client/utilities";
import {features} from "../features";
import {Endpoints, Environment} from "../runtime";
import {v4 as uuidv4} from "uuid";
import {RetryLink} from "@apollo/client/link/retry";
import {onError} from "@apollo/client/link/error";

// Add this in the GraphqlClientProvider component
const retryLink = new RetryLink({
  delay: {
    initial: 300, // Start with 300ms delay
    max: 2000, // Max delay of 5 seconds
    jitter: true // Randomize delay to prevent thundering herd
  },
  attempts: {
    max: 3, // Retry up to 3 times
    retryIf: (error, _operation) => {
      // Retry on network errors and gateway errors
      return (
        !!error &&
        (error.message === "Failed to fetch" || // Happens when the browser cannot reach the server (network down, CORS, DNS, etc.)
          error.message.includes("NetworkError") || // Generic network error, often due to connectivity issues or server unreachable
          error.message.includes("ECONNREFUSED") || // Server actively refused the connection (service not running or firewall)
          error.message.includes("ETIMEDOUT") || // Connection attempt timed out (server too slow or not responding)
          error.message.includes("ENOTFOUND") || // DNS resolution failed (domain name does not exist or DNS server issue)
          error.statusCode === 502 || // Bad Gateway (server received invalid response from upstream)
          error.statusCode === 503 || // Service Unavailable (server is overloaded or down for maintenance)
          error.statusCode === 504) // Gateway Timeout (server did not receive a timely response from upstream)
      );
    }
  }
});

// Add this helper function
const createTimeoutFetch = (timeout: number) => {
  return (input: RequestInfo | URL, options: RequestInit = {}) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    return fetch(input, {
      ...options,
      signal: controller.signal
    })
      .finally(() => clearTimeout(id))
      .catch(error => {
        if (error.name === "AbortError") {
          throw new Error(
            `Request timeout after ${timeout}ms for ${input.toString()}`
          );
        }
        throw error;
      });
  };
};

/**
 * {@link GraphqlClientProvider} provides the Apollo Client to all downstream components
 * allowing them to call backend endpoints. Endpoints are provided the environment and
 * limited to those declared in {@link Endpoints}. The client is initialized with a terminating
 * HttpLink which checks what endpoint was sent in the operation's context and chooses the correct
 * url based on that endpoint.
 * See https://www.apollographql.com/docs/react/api/link/introduction for link documentation
 * and https://www.apollographql.com/docs/react/api/core/ApolloClient for client documentation.
 *
 * New data sources (read: endpoints) will need to be added to {@link Endpoints}, the switch statement below,
 * {@link environments}, and {@link Backends}.
 * @param children
 * @constructor
 */
export const GraphqlClientProvider: FC<PropsWithChildren> = ({children}) => {
  const environment = useEnvironment<Environment>();
  const {authState} = useOktaAuth();
  const flags = useFlags();
  const connectToDevTools = flags[features.ops.apolloClientConnectToDevTools];

  const setCorrelationIdHeader = new ApolloLink((operation, forward) => {
    operation.setContext(() => ({
      headers: {"X-CoxAuto-Correlation-Id": uuidv4()}
    }));
    return forward(operation);
  });

  // Use useRef to store the latest tokens so that we can update it for the authLink while allowing updates to get through to the
  // memoized version of the client
  const latestAuthState = useRef(authState);
  useEffect(() => {
    latestAuthState.current = authState;
  }, [authState]);

  // Set this up outside the memo hook so that we don't re-init the client when the access or id token changes, using the
  // link method will allow us to update the tokens on each request without creating the client and invalidating caches.
  // If the env updates we want to re-init the client, but that will happen due to the useEnvironment hook used by the useMemo
  const authLink = new ApolloLink((operation, forward) => {
    // this is an ugly hack here, but there is a first render race condition where the Apollo client is being used
    // prior to the useEffect above from firing.  So if the latestAuthState is null, fallback to the current authSate,
    // future renders will use the latestAuthState.
    const token =
      latestAuthState.current?.accessToken?.accessToken ||
      authState?.accessToken?.accessToken;
    const idToken =
      latestAuthState.current?.idToken?.idToken || authState?.idToken?.idToken;
    operation.setContext(({headers = {}}) => ({
      headers: {
        ...headers,
        "authorization": token ? `Bearer ${token}` : "",
        "X-Bridge-Id": idToken ? `Bearer ${idToken}` : ""
      }
    }));
    return forward(operation);
  });

  const errorLink = onError(({networkError, graphQLErrors}) => {
    if (networkError) {
      console.error("[Apollo Network Error]", networkError);
    }
    if (graphQLErrors) {
      graphQLErrors.forEach(({message, locations, path}) => {
        console.error("[Apollo GraphQL Error]", message, {locations, path});
      });
    }
  });

  const client = useMemo<ApolloClient<NormalizedCacheObject>>(() => {
    const switchEndpoint = (endpoint: Endpoints) => {
      switch (endpoint) {
        // TODO: Deprecate
        case Endpoints.BACKEND: {
          return `${environment.backend.baseUri}/graphql`;
        }
        case Endpoints.APPSYNC: {
          return `${environment.appsync}`;
        }
        case Endpoints.SPRING_BFF: {
          return `${environment.springBff}`;
        }
        default: {
          return `${environment.backend.baseUri}/graphql`;
        }
      }
    };

    // Get the current token for AppSync auth
    // Create the HTTP link for queries and mutations
    const httpLink = new HttpLink({
      uri: operation => {
        const endpoint = operation.getContext().endpoint;
        return switchEndpoint(endpoint);
      },
      fetchOptions: {
        // Note: fetchOptions.timeout is not standard, need custom fetch
      },
      fetch: createTimeoutFetch(30000) // 30 second timeout
    });

    // Create the AppSync subscription link with custom Lambda authorization
    // The AppSync API uses AWS_LAMBDA authentication type (discovered from Terraform)
    const appSyncSubscriptionLink = createSubscriptionHandshakeLink({
      url: environment.appsync,
      region: window.AWS_REGION,
      auth: {
        type: AUTH_TYPE.AWS_LAMBDA,
        token: () => {
          const token =
            latestAuthState.current?.accessToken?.accessToken ||
            authState?.accessToken?.accessToken;
          return `Bearer ${token}`;
        }
      }
    });

    // Use split to route subscription operations to the subscription link
    // and all other operations to the HTTP link
    const splitLink = split(
      // Check if this is a subscription operation
      ({query}) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      appSyncSubscriptionLink, // Use subscription link for subscription operations
      httpLink // Use HTTP link for queries and mutations
    );

    return new ApolloClient({
      // Conditionally include errorLink and retryLink based on enableAdvancedInstrumentationAndRetryLogic
      link: from([
        removeTypenameFromVariables(),
        setCorrelationIdHeader,
        authLink,
        errorLink,
        retryLink,
        splitLink
      ]),
      cache: new InMemoryCache({
        typePolicies: {
          Oem: {
            keyFields: [
              "abbreviation",
              "id",
              "nameplateDescription",
              "locationId"
            ]
          },
          Nameplate: {
            keyFields: [
              "oemAbbreviation",
              "oemId",
              "nameplateDesc",
              "oemLocationId"
            ]
          }
        }
      }),
      devtools: {
        enabled: connectToDevTools
      }
    });

    // Let's have authSate.isAuthenticated in here so that we will re-init when the auth state changes to true on initial load
    // Ideally we would only return a valid auth provider when the auth state is valid, but we can't do that here without some
    // ux work to show a loading spinner for this component.
    //
    // Let's also disable the exhaustive deps rule here because we don't want to re-init the client
    // when the authLink function is updated.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [environment, authState?.isAuthenticated, connectToDevTools]);

  // If you return the context before the auth is complete, it leads to race conditions on Apollo client for the first render of child components that use the client
  // This does not affect the rendering of loading state as the ActivityIndicator is still shown as a higher component
  return !latestAuthState ? null : (
    <ApolloProvider client={client}>{children}</ApolloProvider>
  );
};
